import { useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  Divider,
  Button,
  useTheme,
  alpha,
  Fade,
} from "@mui/material";
import {
  BatteryAlert,
  BatteryFull,
  ChevronRight,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
} from "@mui/icons-material";
import { useLowBatteryDevices } from "../../hooks/useLowBatteryDevices";
import { useTranslation } from "react-i18next";
import { logger } from "../../utils/logger";

const LowBatteryPanel = memo(({ devices }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const {
    lowBatteryDevices,
    criticalBatteryDevices,
    totalLowBattery,
    totalCriticalBattery,
  } = useLowBatteryDevices(devices);

  const handleDeviceClick = useCallback(
    (deviceId) => {
      navigate(`/device/${deviceId}`);
    },
    [navigate]
  );

  const handleViewAllClick = useCallback(() => {
    // Navigate to devices page with battery filter
    // Test missing key - this should trigger the debugger
    logger.warn("Testing missing key:", t("test.missing_key_for_debugging"));
    navigate("/device?battery=low");
  }, [navigate, t]);

  // Combine and limit devices shown (show critical first, then low)
  const devicesToShow = [
    ...criticalBatteryDevices.slice(0, 3),
    ...lowBatteryDevices.slice(
      0,
      Math.max(0, 5 - criticalBatteryDevices.length)
    ),
  ];

  const totalDevicesWithLowBattery = totalLowBattery + totalCriticalBattery;

  if (totalDevicesWithLowBattery === 0) {
    return (
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
                : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
            backdropFilter: "blur(10px)",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.1)"
            }`,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            {/* Top accent bar */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: theme.palette.primary.main,
                opacity: 0.8,
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
              }}
            >
              <BatteryFull />
            </Box>
            <Box>
              <Typography variant="subtitle1" component="h2" fontWeight={600}>
                {t("common.battery_status")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("common.all_devices_healthy", {
                  defaultValue: "All devices healthy",
                })}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2, opacity: 0.5 }} />

          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            sx={{
              background: alpha(theme.palette.success.main, 0.03),
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <CheckCircle
                sx={{ fontSize: 32, color: theme.palette.success.main }}
              />
            </Box>
            <Typography
              variant="body1"
              color="text.primary"
              fontWeight={600}
              gutterBottom
            >
              {t("common.all_batteries_good")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("common.no_devices_need_charging", {
                defaultValue: "No devices need charging",
              })}
            </Typography>
          </Box>
        </Paper>
      </Fade>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
            : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.1)"
        }`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background:
            totalCriticalBattery > 0
              ? `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.warning.main})`
              : theme.palette.warning.main,
          opacity: 0.8,
        }}
      />

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        mt={1}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(
                totalCriticalBattery > 0
                  ? theme.palette.error.main
                  : theme.palette.warning.main,
                0.1
              ),
              color:
                totalCriticalBattery > 0
                  ? theme.palette.error.main
                  : theme.palette.warning.main,
            }}
          >
            <BatteryAlert />
          </Box>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" component="h2" fontWeight={600}>
                {t("common.low_battery_devices")}
              </Typography>
              <Chip
                label={totalDevicesWithLowBattery}
                size="small"
                sx={{
                  height: 22,
                  minWidth: 28,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  backgroundColor:
                    totalCriticalBattery > 0
                      ? theme.palette.error.main
                      : theme.palette.warning.main,
                  color: "#fff",
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t("common.devices_need_attention", {
                defaultValue: "Devices need attention",
              })}
            </Typography>
          </Box>
        </Box>

        {/* Summary chips */}
        <Box display="flex" gap={1}>
          {totalCriticalBattery > 0 && (
            <Chip
              size="small"
              icon={<ErrorIcon sx={{ fontSize: 16 }} />}
              label={`${totalCriticalBattery} Critical`}
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                fontWeight: 600,
                "& .MuiChip-icon": {
                  color: theme.palette.error.main,
                },
              }}
            />
          )}
          {totalLowBattery > 0 && (
            <Chip
              size="small"
              icon={<Warning sx={{ fontSize: 16 }} />}
              label={`${totalLowBattery} Low`}
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                fontWeight: 600,
                "& .MuiChip-icon": {
                  color: theme.palette.warning.main,
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2, opacity: 0.5 }} />

      {/* Device List */}
      <List sx={{ py: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {devicesToShow.map((device, index) => {
          const isCritical = device.batteryPercent <= 10;
          const batteryColor = isCritical
            ? theme.palette.error.main
            : theme.palette.warning.main;

          return (
            <Fade in timeout={300 + index * 50} key={device.id}>
              <ListItem
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  borderLeft: `4px solid ${batteryColor}`,
                  transition: "all 0.2s ease",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.6)
                      : theme.palette.background.paper,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 2px 8px rgba(0,0,0,0.2)"
                      : "0 2px 8px rgba(0,0,0,0.04)",
                  "&:hover": {
                    backgroundColor: alpha(batteryColor, 0.06),
                    transform: "translateX(4px)",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 4px 12px rgba(0,0,0,0.3)"
                        : "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
                onClick={() => handleDeviceClick(device.id)}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    bgcolor: alpha(batteryColor, 0.1),
                    color: batteryColor,
                  }}
                >
                  {isCritical ? <ErrorIcon /> : <Warning />}
                </Avatar>

                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {device.name || `Device ${device.id}`}
                    </Typography>
                  }
                  secondary={
                    <Box
                      component="span"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mt={0.25}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Battery: {device.batteryPercent}%
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          width: 3,
                          height: 3,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.text.disabled,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {device.batteryVoltage}mV
                      </Typography>
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <ChevronRight
                    sx={{
                      fontSize: 20,
                      color: theme.palette.text.secondary,
                      opacity: 0.5,
                    }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Fade>
          );
        })}
      </List>

      {/* View All Button */}
      {totalDevicesWithLowBattery > 5 && (
        <Box mt={3} textAlign="center">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleViewAllClick}
            size="small"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 3,
            }}
          >
            View All {totalDevicesWithLowBattery} Devices
          </Button>
        </Box>
      )}
    </Paper>
  );
});

LowBatteryPanel.displayName = "LowBatteryPanel";

export default LowBatteryPanel;
